import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const EMPTY_PAGE = { page: 1, page_size: 100, total: 0, total_pages: 1 };
const CACHE_TTL = 30000;

export default function useInspectionList(api, navigate) {
  const [query, setQuery] = useState(() => ({
    month: String(new Date().getMonth() + 1).padStart(2, '0'),
    billing: 'all', search: '', date_from: '', date_to: '', tech: '', page: 1,
  }));
  const [data, setData] = useState({ inspections: [], can_bill: false, pagination: EMPTY_PAGE });
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const cache = useRef(new Map());

  const updateQuery = useCallback((changes) => {
    setQuery((previous) => {
      const next = { ...previous, ...changes };
      if (Object.keys(changes).some((key) => key !== 'page' && next[key] !== previous[key])) next.page = 1;
      return Object.keys(next).every((key) => next[key] === previous[key]) ? previous : next;
    });
  }, []);

  const reload = useCallback(() => {
    cache.current.clear();
    setRefresh((value) => value + 1);
  }, []);

  const updateRows = useCallback((update) => {
    cache.current.clear();
    setData((previous) => ({ ...previous, inspections: update(previous.inspections) }));
  }, []);

  useEffect(() => {
    const key = JSON.stringify([api, query]);
    const cached = cache.current.get(key);
    if (cached && Date.now() - cached.savedAt < CACHE_TTL) {
      setData(cached.data);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    axios.get(api, {
      params: { getInspections: 'all', paginated: 1, page_size: 100, ...query },
      signal: controller.signal,
    }).then((response) => {
      if (controller.signal.aborted) return;
      const result = response.data;
      if (!Array.isArray(result?.inspections) || !result.pagination) {
        throw new Error('Invalid inspection list response.');
      }
      if (cache.current.size >= 20) cache.current.delete(cache.current.keys().next().value);
      cache.current.set(key, { data: result, savedAt: Date.now() });
      setData(result);
      if (result.pagination.page !== query.page) {
        const adjusted = { ...query, page: result.pagination.page };
        cache.current.set(JSON.stringify([api, adjusted]), { data: result, savedAt: Date.now() });
        setQuery(adjusted);
      }
    }).catch((error) => {
      if (controller.signal.aborted || error.code === 'ERR_CANCELED') return;
      if (error.response?.status === 401) navigate('/login');
      else toast.error(error.response?.data?.error || 'Error fetching inspections', { position: 'top-center' });
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [api, navigate, query, refresh]);

  return { query, updateQuery, reload, updateRows, inspections: data.inspections,
    noAccess: !data.can_bill, pagination: data.pagination, loading };
}
